import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MaintenanceRoutesService } from '../services/routes.service';
import type { 
  MaintenanceRoute, 
  RouteFilters 
} from '../types/routes.types';
import { useState } from 'react';

export function useMaintenanceRoutes(companyId: string, initialFilters: RouteFilters = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<RouteFilters>({
    status: 'all',
    ...initialFilters
  });

  const query = useQuery({
    queryKey: ['maintenance-routes', companyId, filters],
    queryFn: () => MaintenanceRoutesService.getRoutes(companyId, filters),
    enabled: !!companyId,
  });

  const statsQuery = useQuery({
    queryKey: ['maintenance-routes-stats', companyId],
    queryFn: () => MaintenanceRoutesService.getRouteStats(companyId),
    enabled: !!companyId,
  });

  const createRouteMutation = useMutation({
    mutationFn: (data: Partial<MaintenanceRoute>) => MaintenanceRoutesService.createRoute(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-routes'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-routes-stats'] });
    },
  });

  const updateRouteMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string, updates: Partial<MaintenanceRoute> }) => 
      MaintenanceRoutesService.updateRoute(id, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-routes'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-routes', 'detail', variables.id] });
    },
  });

  return {
    ...query,
    routes: query.data?.data || [],
    total: query.data?.total || 0,
    stats: statsQuery.data,
    isLoadingStats: statsQuery.isLoading,
    filters,
    setFilters,
    createRoute: createRouteMutation.mutateAsync,
    updateRoute: updateRouteMutation.mutateAsync,
    isCreating: createRouteMutation.isPending,
    isUpdating: updateRouteMutation.isPending,
  };
}

export function useMaintenanceRouteDetail(id?: string) {
  return useQuery({
    queryKey: ['maintenance-routes', 'detail', id],
    queryFn: () => MaintenanceRoutesService.getRouteDetail(id!),
    enabled: !!id,
  });
}

export function useRouteStops(routeId?: string) {
  return useQuery({
    queryKey: ['maintenance-route-stops', routeId],
    queryFn: () => MaintenanceRoutesService.getRouteStops(routeId!),
    enabled: !!routeId,
  });
}

export function useRouteLogs(assignmentId?: string) {
  return useQuery({
    queryKey: ['maintenance-route-logs', assignmentId],
    queryFn: () => MaintenanceRoutesService.getRouteLogs(assignmentId!),
    enabled: !!assignmentId,
  });
}
